import React, { useRef, useEffect } from 'react';
import { IonModal, IonButton, IonInput } from '@ionic/react';

import '../../css/alert-modal.css';

interface AlertModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  title?: string;
  subtitle?: string;
  message?: string;
  component?: React.ReactNode;
  buttons?: Array<{
    text: string;
    role?: 'cancel' | 'destructive' | 'primary';
    handler?: () => void;
  }>;
  inputs?: Array<{
    name: string;
    type?: string;
    placeholder?: string;
    value?: string;
  }>;
}

const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  onDidDismiss,
  title,
  subtitle,
  message,
  component,
  buttons = [{ text: 'OK' }],
  inputs = []
}) => {
  const modalRef = useRef<HTMLIonModalElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Wait for modal to be fully rendered
      setTimeout(() => {
        const content = contentRef.current;
        if (content) {
          const contentHeight = content.scrollHeight;
          const modal = modalRef.current;

          if (modal) {
            // Set a custom CSS property for the height
            modal.style.setProperty(
              '--height',
              `${Math.min(contentHeight, window.innerHeight * 0.9)}px`
            );
          }
        }
      }, 100);
    }
  }, [isOpen]);

  const handleButtonClick = (button: any) => {
    if (button.handler) {
      button.handler();
    }
  };

  const getButtonClass = (role?: string) => {
    switch (role) {
      case 'destructive':
        return 'alert-modal-button button-danger';
      case 'cancel':
        return 'alert-modal-button button-secondary';
      case 'primary':
      default:
        return 'alert-modal-button button-primary';
    }
  };

  return (
    <IonModal
      ref={modalRef}
      cssClass='alert-modal'
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
    >
      <div ref={contentRef} className='alert-modal-content'>
        {/* Header */}
        {(title || subtitle) && (
          <div className='alert-modal-header'>
            {title && <h2 className='alert-modal-title'>{title}</h2>}
            {subtitle && <p className='alert-modal-subtitle'>{subtitle}</p>}
          </div>
        )}

        {/* Message */}
        {message && <div className='alert-modal-message'>{message}</div>}

        {/* Component */}
        {component && <div className='alert-modal-component'>{component}</div>}

        {/* Inputs */}
        {inputs.length > 0 && (
          <div className='alert-modal-inputs'>
            {inputs.map((input, index) => (
              <IonInput
                key={index}
                className='alert-modal-input'
                placeholder={input.placeholder}
                value={input.value}
                name={input.name}
              />
            ))}
          </div>
        )}

        {/* Buttons */}
        <div
          className={`alert-modal-buttons ${
            buttons.length > 1 && buttons.length <= 2 ? 'horizontal' : ''
          }`}
        >
          {buttons.map((button, index) => (
            <IonButton
              key={index}
              fill='clear'
              expand='full'
              className={getButtonClass(button.role)}
              onClick={() => handleButtonClick(button)}
            >
              {button.text}
            </IonButton>
          ))}
        </div>
      </div>
    </IonModal>
  );
};

export default AlertModal;
